import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-probleme";
const TITLE = "Shopify Probleme lösen: die häufigsten Fälle im Überblick";
const DESCRIPTION =
  "Typische Shopify Probleme und ihre Ursachen: Theme-Fehler nach Updates, langsame Ladezeiten, Zahlungsarten, Versandregeln, Varianten und Checkout. Mit Lösungswegen und Kostenrahmen.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Die meisten Shopify Probleme haben drei Ursachen: eine App, die etwas überschreibt, eine Theme-Anpassung, die ein Update nicht überlebt hat, oder eine Einstellung in Versand, Steuern oder Zahlungsarten. Wer in dieser Reihenfolge prüft, findet die Ursache meist in unter einer Stunde, statt im Theme herumzuraten.";

const faqs = [
  {
    q: "Mein Shopify Theme ist nach einem Update kaputt. Was tun?",
    a: "Anpassungen am Theme-Code gehen bei einem Update verloren, wenn sie direkt in der Vorlage lagen. Sinnvoll ist, das alte Theme als Kopie zu behalten, die Anpassungen zu dokumentieren und sie im neuen Theme als eigene Sections oder Snippets erneut anzulegen.",
  },
  {
    q: "Warum ist mein Shopify Shop langsam?",
    a: "In den allermeisten Fällen liegt es an Apps, die auf jeder Seite eigene Skripte laden, und an unkomprimierten Bildern. Erst danach lohnt sich ein Blick auf das Theme selbst.",
  },
  {
    q: "Warum erscheint eine Zahlungsart im Checkout nicht?",
    a: "Häufige Gründe sind Länderbeschränkungen des Anbieters, ein nicht abgeschlossenes Verifizierungsverfahren, Mindest- oder Höchstbeträge oder eine Währung, die der Anbieter für Ihr Konto nicht freigeschaltet hat.",
  },
  {
    q: "Warum stimmen Versandkosten oder Steuern nicht?",
    a: "Versandprofile und Zonen greifen produktabhängig. Wenn ein Produkt in einem eigenen Versandprofil liegt oder ein Land in keiner Zone steht, entstehen genau die Ergebnisse, die im Checkout überraschen.",
  },
  {
    q: "Was kostet die Behebung?",
    a: "Klar abgrenzbare Fehler liegen meist bei ein bis drei Stunden. Ich rechne mit 110 € netto pro Stunde ab und nenne nach der Sichtung einen Rahmen, bevor ich anfange.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-probleme")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Support",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Support"
      title="Shopify Probleme: Ursache finden statt im Theme raten"
      intro="Fast jeder Fehler im Shop lässt sich auf wenige Quellen zurückführen. Dieser Überblick zeigt die häufigsten Fälle, die Reihenfolge bei der Eingrenzung und wann externe Hilfe günstiger ist als weiter zu suchen."
      source="ratgeber-shopify-probleme"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Die Reihenfolge bei der Fehlersuche",
          p: [
            "Bevor Sie Code anfassen, grenzen Sie ein. Eine deaktivierte App, ein Test im unveränderten Standard-Theme und ein Blick in die Einstellungen beantworten die meisten Fragen schneller als jede Vermutung.",
          ],
          list: [
            "Tritt der Fehler auch in einer Theme-Kopie ohne Anpassungen auf?",
            "Verschwindet er, wenn eine kürzlich installierte App deaktiviert wird?",
            "Betrifft er alle Produkte oder nur einzelne mit abweichenden Daten?",
            "Tritt er nur auf dem Handy, nur im Checkout oder nur in einem Land auf?",
            "Wurde kurz davor etwas veröffentlicht, importiert oder aktualisiert?",
          ],
        },
        {
          h: "Theme-Fehler nach Updates",
          p: [
            "Anpassungen, die direkt in Standarddateien geschrieben wurden, überleben ein Theme-Update nicht. Was danach fehlt, war meist nie versionssicher angelegt.",
            "Sauber ist der umgekehrte Weg: eigene Sections und Snippets, die ein Update unberührt lässt. Das kostet beim ersten Mal etwas mehr Zeit und danach nie wieder.",
          ],
        },
        {
          h: "Langsame Ladezeit",
          p: [
            "Langsam werden Shops selten durch das Theme, sondern durch die Summe der Apps. Jede lädt eigene Skripte, oft auf allen Seiten, auch dort, wo ihre Funktion gar nicht gebraucht wird.",
            "Zweiter großer Posten sind Bilder in Originalgröße und mehrere eingebundene Schriftfamilien. Beides lässt sich ohne Theme-Neubau bereinigen.",
          ],
        },
        {
          h: "Checkout, Versand und Zahlungsarten",
          p: [
            "Probleme im Checkout wirken dramatisch, sind aber fast immer Konfiguration: Versandprofile, in denen einzelne Produkte hängen, Zonen ohne passendes Land, Steuereinstellungen oder ein Zahlungsanbieter mit offener Verifizierung.",
            "Weil hier direkt Umsatz hängt, lohnt sich eine strukturierte Prüfung statt schrittweisem Ausprobieren im Live-Shop.",
          ],
        },
        {
          h: "Wann sich Hilfe rechnet",
          p: [
            "Wenn die Eingrenzung länger dauert als die Behebung, ist der Punkt erreicht. Klar beschreibbare Fehler liegen meist bei ein bis drei Stunden zu 110 € netto pro Stunde, inklusive kurzer Erklärung, woran es lag.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-support", label: "Shopify Support anfragen" },
        { to: "/ratgeber/shopify-pagespeed-optimieren", label: "Ladezeit verbessern" },
        { to: "/shopify-theme-anpassung", label: "Theme sauber anpassen" },
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren" },
        { to: "/kontakt", label: "Problem schildern" },
      ]}
    />
  );
}
